import { FC } from 'react';

import { DELIVERY_CHANNEL_CONFIG } from '@shared/api/giftCoupon/giftCoupon.interface';

import { wholePriceSuggestions } from '../utils/chargeEstimate.utils';
import { CopyPhonesConfirmState } from '../hooks/useRedemptionPage';

interface Props {
  state: CopyPhonesConfirmState;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * 번호 복사 확인 모달.
 *
 * 복사한 번호는 그대로 발송매체에 붙여넣어 "한 상품"으로 일괄 구매된다.
 * 매체 구매는 되돌릴 수 없으므로 클립보드에 넣기 전에 어떤 상품인지 한 번 못박는다.
 * (선택 잠금으로 상품이 섞이는 경우는 이미 막혀 있고, 여기는 대상 확인 단계)
 */
export const CopyPhonesConfirmModal: FC<Props> = ({ state, onCancel, onConfirm }) => {
  const preview = state.phones.slice(0, 3);
  const count = state.phones.length;

  // 발주 전 예상 금액 — 세 방식(최근 발송가 / 정가 감액 / 등록 도매가)을 우선순위대로 나란히.
  // 감액률은 예측 매체 기준(매체는 발송처리 때 확정)
  const suggestions = wholePriceSuggestions(state, state.recentChannel);

  // 어느 발주처(매체)에서 사야 하는지 — 발송 URL / 직전 발송 두 근거
  const orderChannel = state.urlChannel ?? state.recentDeliveredChannel;
  const channelConflict =
    !!state.urlChannel &&
    !!state.recentDeliveredChannel &&
    state.urlChannel !== state.recentDeliveredChannel;
  const channelBasis = channelConflict
    ? '발송 URL·직전 발송 불일치'
    : state.urlChannel && state.recentDeliveredChannel
    ? '발송 URL·직전 발송 일치'
    : state.urlChannel
    ? '발송 URL 기준'
    : '직전 발송 기준';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl">
        <div className="flex items-baseline justify-between">
          <h3 className="text-base font-bold text-gray-900">번호 복사</h3>
          <span className="text-sm font-semibold text-gray-500">
            {state.phones.length}건
          </span>
        </div>

        {/* 발주 대상 상품 — 이 화면에서 제일 크게 */}
        <div className="mt-4 rounded-lg border-2 border-indigo-200 bg-indigo-50 px-4 py-3">
          <p className="text-[11px] font-semibold text-indigo-500">발주 상품</p>
          <p className="mt-0.5 break-keep text-lg font-bold leading-snug text-indigo-900">
            {state.label}
          </p>
          {orderChannel && (
            <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-indigo-500">발주처</span>
              <span
                className={`inline-flex rounded-full border px-1.5 py-0.5 text-[11px] font-bold ${DELIVERY_CHANNEL_CONFIG[orderChannel].badgeClass}`}
              >
                {DELIVERY_CHANNEL_CONFIG[orderChannel].label}
              </span>
              <span
                className={
                  channelConflict ? 'font-bold text-amber-700' : 'text-indigo-400'
                }
              >
                {channelBasis}
              </span>
            </p>
          )}
        </div>

        {/* 예상 발주 금액 — 매체에서 얼마를 결제해야 하는지 미리 대조 */}
        {suggestions.length > 0 && (
          <div className="mt-2 space-y-1 rounded-lg border border-gray-200 px-3 py-2 text-xs">
            {suggestions.map((s) => (
              <div key={s.kind} className="flex items-center justify-between gap-2">
                <span className="text-gray-500">
                  {s.label} · {s.hint}
                </span>
                <span className="text-gray-700">
                  {s.price.toLocaleString()}원 ×{count} ={' '}
                  <span className="font-bold text-gray-900">
                    {(s.price * count).toLocaleString()}원
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}

        <p className="mt-3 text-sm font-medium text-red-700">
          이 번호는 위 상품으로만 발주하세요. 매체 구매는 되돌릴 수 없습니다.
        </p>

        {state.skippedCount > 0 && (
          <p className="mt-2 text-xs font-medium text-amber-700">
            번호 없는 {state.skippedCount}건은 복사에서 빠집니다.
          </p>
        )}

        <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 font-mono text-xs text-gray-600">
          {preview.map((p) => (
            <div key={p}>{p}</div>
          ))}
          {state.phones.length > preview.length && (
            <div className="text-gray-400">
              외 {state.phones.length - preview.length}건
            </div>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700"
          >
            복사
          </button>
        </div>
      </div>
    </div>
  );
};
